import React, { memo, useMemo } from "react";

import { useAppRouteNavigation } from "@/app/app/_/routing";
import { getPendingChangesStats } from "@/components/molecules/SelectableListItemRow";
import {
  THREAD_LIST_FILTERS,
  THREAD_LIST_PAGE_SIZE,
} from "@/components/organisms/Thread/Thread.content";
import { Navigation, Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { State } from "@/state";
import type { SidebarItem, SidebarRoutingItem } from "@/constants/navigation";
import { AppViewsType } from "@/state/@types";
import { AppSidebarDividerItem } from "@/layouts/AppLayout/AppSidebar/AppSidebarDividerItem";
import { AppSidebarEntitySettingsPopper } from "@/layouts/AppLayout/AppSidebar/AppSidebarEntitySettingsPopper";
import { AppSidebarOverlineItem } from "@/layouts/AppLayout/AppSidebar/AppSidebarOverlineItem";
import { AppSidebarCommandQuickActionsItem } from "@/layouts/AppLayout/AppSidebar/AppSidebarQuickActionsItem";
import { RoutingItem } from "@/layouts/AppLayout/AppSidebar/AppSidebarRoutingItem";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

export type AppSidebarResolveItemProps = (item: SidebarRoutingItem) => {
  isActive?: boolean;
  onClick?: () => void;
};

type AppSidebarProps = {
  header?: React.ReactNode;
  showQuickActions?: boolean;
  items?: SidebarItem[];
  resolveItemProps?: AppSidebarResolveItemProps;
};

const AppSidebar = memo(function AppSidebar({
  header,
  showQuickActions = true,
  items = Navigation.SIDEBAR,
  resolveItemProps,
}: AppSidebarProps) {
  const { navigateToView } = useAppRouteNavigation();
  const activeEntityIdentifiers = State(
    (state) => state.activeEntityIdentifiers,
  );
  // Settings swaps in its own items, which carry neither count.
  const showCounts = items === Navigation.SIDEBAR;
  const lastDivider = items.map((item) => item.type).lastIndexOf("divider");

  // Same variables as the triage view's opening page and the thread list's
  // pending tab, so the counts share their cache entries: refetches there move
  // the numbers here, and loading the counts warms those lists.
  const { data: triageData, loading: triageLoading } = useBackendQuery(
    Queries.TRIAGE_CONNECTION_QUERY,
    {
      variables: { entityIdentifiers: activeEntityIdentifiers, first: 25 },
      fetchPolicy: "cache-and-network",
      skip: !showCounts,
    },
  );
  const { data: pendingThreadsData, loading: pendingThreadsLoading } =
    useBackendQuery(Queries.THREADS_CONNECTION_QUERY, {
      variables: {
        filter: THREAD_LIST_FILTERS.pending,
        first: THREAD_LIST_PAGE_SIZE,
      },
      fetchPolicy: "cache-and-network",
      skip: !showCounts,
    });

  const triageCount = triageData?.triageConnection?.data?.totalCount ?? 0;

  // Only the first load: a background refetch keeps the number it has.
  const countSkeleton = (
    <Skeleton
      variant="rectangular"
      width={22}
      height={18}
      className="rounded"
    />
  );

  const pendingThreads = useMemo(() => {
    const connection = pendingThreadsData?.threadsConnection?.data;
    const edges = connection?.edges ?? [];
    const stats = getPendingChangesStats(
      edges.flatMap((edge) => edge.node.pendingSession?.pendingChanges ?? []),
    );
    return {
      label: `${edges.length}${connection?.pageInfo.hasNextPage ? "+" : ""}`,
      count: edges.length,
      stats,
    };
  }, [pendingThreadsData]);

  const pendingThreadsAdornment =
    pendingThreads.count > 0 ? (
      <div
        aria-label={`${pendingThreads.label} threads with pending changes: ${pendingThreads.stats.added} created, ${pendingThreads.stats.changed} updated`}
        // The breakdown slides out leftward from the count while the row is
        // hovered; ``grid-template-columns`` animates to its natural width.
        className="inline-flex items-center h-4.5 text-[11px] font-medium [line-height:1] [font-variant-numeric:tabular-nums] whitespace-nowrap [&_.breakdown]:grid [&_.breakdown]:grid-cols-[0fr] [&_.breakdown]:opacity-[0] [&_.breakdown]:[transition:grid-template-columns_0.2s_ease-out,opacity_0.15s_ease-out] [[data-testid^='sidebar-nav-']:hover_&_.breakdown]:grid-cols-[1fr] [[data-testid^='sidebar-nav-']:hover_&_.breakdown]:opacity-[1]"
      >
        <div className="breakdown">
          <div className="overflow-hidden flex gap-1.5 pr-1.5">
            {pendingThreads.stats.added > 0 && (
              <Typography
                component="span"
                className="[font:inherit] text-foreground"
              >
                +{pendingThreads.stats.added}
              </Typography>
            )}
            {pendingThreads.stats.changed > 0 && (
              <Typography
                component="span"
                className="[font:inherit] text-foreground"
              >
                ~{pendingThreads.stats.changed}
              </Typography>
            )}
          </div>
        </div>
        <span className="min-w-4.5 h-4.5 pl-1.25 pr-1.25 rounded inline-flex items-center justify-center text-secondary-foreground bg-secondary">
          {pendingThreads.label}
        </span>
      </div>
    ) : null;

  return (
    <>
      <div className="flex flex-col items-start h-[calc(100%)] min-h-[calc(100%)] max-h-[calc(100%)] w-[calc(100%)] flex-nowrap @container [container-name:sidebar]">
        <div className="h-12 [border-bottom-width:1px] [border-bottom-style:solid] border-b-border w-full">
          {header ?? <AppSidebarEntitySettingsPopper />}
        </div>

        {showQuickActions && (
          <div className="pl-2 pr-2 w-full min-h-12 flex items-center [border-bottom-width:1px] [border-bottom-style:solid] border-border">
            <AppSidebarCommandQuickActionsItem />
          </div>
        )}

        <div
          // What follows the last divider - the theme, support - at the
          // bottom where there is room; scrolled to, with the rest, where not.
          className="overflow-y-auto w-full h-full pt-2 pb-2 relative flex flex-col"
        >
          {items.map((item, index) => (
            <React.Fragment key={item.type + index}>
              {index === lastDivider && <div aria-hidden className="grow" />}
              {item.type === "routing" &&
                (() => {
                  const resolved = resolveItemProps?.(item);
                  return (
                    <RoutingItem
                      item={item}
                      isLoading={item.isLoading}
                      badgeCount={
                        showCounts && item.key === "triage" ? triageCount : 0
                      }
                      collapsedDotColor={
                        !showCounts
                          ? null
                          : item.key === "triage" && triageCount > 0
                            ? "danger"
                            : item.key === "thread" && pendingThreads.count > 0
                              ? "neutral"
                              : null
                      }
                      endAdornment={
                        !showCounts
                          ? null
                          : item.key === "triage"
                            ? triageLoading && !triageData
                              ? countSkeleton
                              : null
                            : item.key === "thread"
                              ? pendingThreadsLoading && !pendingThreadsData
                                ? countSkeleton
                                : pendingThreadsAdornment
                              : null
                      }
                      isActive={resolved?.isActive}
                      onClick={
                        resolved?.onClick ??
                        (item.onClick
                          ? item.onClick
                          : () => {
                              navigateToView(item.key as AppViewsType, {
                                ...(item.key === "reports"
                                  ? { viewId: null }
                                  : {}),
                                preservePanel: true,
                                preserveQuery: false,
                              });
                            })
                      }
                    />
                  );
                })()}
              {item.type === "divider" && (
                <AppSidebarDividerItem
                  item={item}
                  index={index}
                  isBeforeOverline={items[index + 1]?.type === "overline"}
                />
              )}
              {item.type === "overline" && (
                <AppSidebarOverlineItem
                  item={item}
                  index={index}
                  isAfterDivider={items[index - 1]?.type === "divider"}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
});

export { AppSidebar };
